// Local, per-user "recently forwarded to" list backing the forward dialog's Recent section.
// Client-only, localStorage, namespaced per signed-in user (same recipe as
// pinned-messages-store, minus the change-event machinery — the dialog re-reads on open).
//
// Entries snapshot what a picker row needs (kind + id + label, phone for patients) so the
// Recent rows render instantly, before any directory or search round trip.

import { getAuthUser } from "@/lib/api/token-store";

const STORAGE_KEY_PREFIX = "healo-forward-recents";
// Keep more than the dialog shows: entries whose target no longer resolves (partner left the
// chat directory, group deleted) are skipped at render time, so the tail acts as backfill.
const MAX_STORED = 10;

function canUseBrowserStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function storageKey() {
  const uid = String(getAuthUser()?.id || "").trim();
  return uid ? `${STORAGE_KEY_PREFIX}:${uid}` : null;
}

function normalizeEntry(value) {
  if (!value || typeof value !== "object") return null;
  const kind = value.kind === "partner" || value.kind === "group" || value.kind === "patient" ? value.kind : null;
  const id = String(value.id || "").trim();
  if (!kind || !id) return null;
  return {
    kind,
    id,
    label: String(value.label || "").trim(),
    phone: String(value.phone || "").trim(),
    usedAt: String(value.usedAt || "").trim(),
  };
}

/** Most recently used forward targets for the signed-in user, newest first. */
export function getRecentForwardTargets() {
  const key = storageKey();
  if (!key || !canUseBrowserStorage()) return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeEntry).filter(Boolean);
  } catch {
    return [];
  }
}

/** Records a successful forward's destination; dedups by kind+id and caps the list. */
export function recordForwardTarget({ kind, id, label, phone } = {}) {
  const entry = normalizeEntry({ kind, id, label, phone, usedAt: new Date().toISOString() });
  const key = storageKey();
  if (!entry || !key || !canUseBrowserStorage()) return;
  const rest = getRecentForwardTargets().filter(
    (existing) => !(existing.kind === entry.kind && existing.id === entry.id),
  );
  try {
    window.localStorage.setItem(key, JSON.stringify([entry, ...rest].slice(0, MAX_STORED)));
  } catch {
    // storage quota / private mode — recents are a convenience, never worth an error
  }
}
